import { useVoiceToText, type VttRecording } from '../../../hooks/useVoiceToText';
import { NavIcon } from '../../../shell/shared';

/* ------------------------------------------------------------------ */
/*  ConversationRecorder (PRD.274 simplification)                     */
/*                                                                    */
/*  The single, explicit recording entry point. One primary action,   */
/*  "Record a new conversation" — every completed recording becomes a  */
/*  new conversation (the parent creates it on completion). Active     */
/*  state uses red only for the recording indicator + Stop; Cancel     */
/*  discards without creating an empty conversation.                   */
/* ------------------------------------------------------------------ */

const RED = '#dc2626';

interface Props {
  /** Fired on a successful recording — the parent creates the conversation. */
  onRecordingComplete: (rec: VttRecording) => void;
  /** Disable when the endpoint isn't configured or the user lacks permission. */
  disabled?: boolean;
  primaryColor: string;
}

export function ConversationRecorder({ onRecordingComplete, disabled, primaryColor }: Props) {
  const vtt = useVoiceToText({
    mode: 'translation',
    summaryStyle: 'chief_complaint',
    onRecordingComplete,
  });

  const elapsed = vtt.elapsed;
  const max = vtt.maxDuration;
  const pct = max > 0 ? Math.min(100, (elapsed / max) * 100) : 0;
  const nearLimit = pct >= 85;
  const remaining = Math.max(0, max - elapsed);

  if (vtt.status === 'recording') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: 'var(--sp-3)',
          border: `1px solid ${RED}`,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: RED, fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>
            <span
              aria-hidden
              style={{ width: 8, height: 8, borderRadius: '50%', background: RED, display: 'inline-block' }}
            />
            Recording new conversation
          </span>
          <span
            aria-live="polite"
            style={{ fontSize: 'var(--font-size-xs)', color: nearLimit ? RED : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}
          >
            {fmt(elapsed)} / {fmt(max)}
            <span style={visuallyHidden}> — {fmt(remaining)} remaining</span>
          </span>
        </div>

        <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }} aria-hidden>
          <div style={{ width: `${pct}%`, height: '100%', background: nearLimit ? RED : primaryColor, transition: 'width 200ms linear' }} />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => void vtt.stop()}
            style={{ ...btn, background: RED, color: '#fff', border: 'none' }}
            aria-label="Stop recording"
          >
            <span aria-hidden style={{ width: 10, height: 10, background: '#fff', borderRadius: 1, display: 'inline-block' }} />{' '}
            Stop recording
          </button>
          <button type="button" onClick={() => vtt.reset()} style={btn} aria-label="Cancel recording">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (vtt.status === 'transcribing') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }} aria-live="polite">
        <span style={{ display: 'inline-flex', animation: 'spin 1s linear infinite' }}>
          <NavIcon name="loader" />
        </span>
        Transcribing…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <button
        type="button"
        onClick={() => vtt.start()}
        disabled={disabled}
        aria-label="Record a new conversation"
        style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 34,
          padding: '0 14px',
          borderRadius: 'var(--radius-sm)',
          border: 'none',
          background: primaryColor,
          color: '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
        }}
      >
        <NavIcon name="mic" /> Record a new conversation
      </button>
      {vtt.status === 'error' && vtt.error && (
        <span role="status" style={{ fontSize: 'var(--font-size-xs)', color: RED }}>
          {vtt.error}
        </span>
      )}
    </div>
  );
}

function fmt(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

const btn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 32,
  padding: '0 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
